/**
 * POS AI Assistant Page
 * =====================
 * 
 * AI-powered retail assistant for business insights.
 */

import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Bot,
  Send,
  ArrowLeft,
  Sparkles,
  Package,
  AlertTriangle,
  Users,
  TrendingUp,
  Loader2,
  History,
  Lightbulb,
  BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSAIAssistantPage = () => {
  const { token } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  useEffect(() => {
    fetchSuggestions();
  }, []);
  
  const fetchSuggestions = async () => {
    try {
      const res = await fetch(`${API}/pos/ai-assistant/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };
  
  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    
    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/pos/ai-assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to get response");
      }
      
      const data = await res.json();
      
      if (data.session_id) {
        setSessionId(data.session_id);
      }
      
      const assistantMessage = {
        role: "assistant",
        content: data.response,
        insights: data.insights,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (err) {
      console.error("Chat error:", err);
      toast.error(err.message || "Failed to get AI response");
      
      // Add error message
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        isError: true,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };
  
  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const renderInsightCard = (insight) => {
    const iconMap = {
      top_products: Package,
      low_stock: AlertTriangle,
      customers: Users,
      margin: TrendingUp,
      sales: BarChart3
    };
    
    const Icon = iconMap[insight.type] || Sparkles;
    
    const colorMap = {
      top_products: "from-indigo-500/20 to-indigo-600/10 border-indigo-500/20",
      low_stock: "from-amber-500/20 to-amber-600/10 border-amber-500/20",
      customers: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/20",
      margin: "from-purple-500/20 to-purple-600/10 border-purple-500/20",
      sales: "from-blue-500/20 to-blue-600/10 border-blue-500/20"
    };
    
    const bgColor = colorMap[insight.type] || "from-slate-500/20 to-slate-600/10 border-slate-500/20";
    
    return (
      <div
        key={insight.type}
        className={`bg-gradient-to-br ${bgColor} rounded-lg border p-3 mt-3`}
      >
        <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4" />
          {insight.title}
        </h4>
        <div className="space-y-1">
          {insight.data.slice(0, 5).map((item, idx) => (
            <div key={idx} className="flex justify-between items-center text-xs">
              <span className="truncate flex-1">
                {item.name || item.customer_name || `Item ${idx + 1}`}
              </span>
              <span className="text-gray-500 ml-2">
                {item.quantity ? `${item.quantity} sold` : ""}
                {item.total_stock !== undefined ? `${item.total_stock} in stock` : ""}
                {item.total_spent ? formatPrice(item.total_spent) : ""}
                {item.margin_percent ? `${item.margin_percent}% margin` : ""}
              </span>
              {item.revenue && (
                <span className="text-emerald-400 ml-2">{formatPrice(item.revenue)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant/pos" className="text-white/80 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2 text-white">
                <Bot className="h-6 w-6 text-fuchsia-200" />
                AI Retail Assistant
              </h1>
              <p className="text-white/70 text-sm">
                Ask questions about your business performance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-300" />
            <span className="text-sm text-white/80">Powered by AI</span>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col max-w-4xl w-full mx-auto">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="h-16 w-16 mx-auto text-indigo-400/50 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Welcome to AI Retail Assistant</h2>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                I can help you understand your sales, inventory, customers, and more. 
                Try asking one of these questions:
              </p>
              
              {/* Suggestion Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="p-4 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl border border-gray-200 text-left hover:border-indigo-500/50 hover:bg-white transition-all group"
                    data-testid={`suggestion-${idx}`}
                  >
                    <Lightbulb className="h-5 w-5 text-amber-400 mb-2 group-hover:text-amber-300" />
                    <p className="text-sm">{suggestion}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] ${
                      message.role === "user"
                        ? "bg-indigo-600 rounded-2xl rounded-br-md"
                        : message.isError
                        ? "bg-red-500/20 border border-red-500/30 rounded-2xl rounded-bl-md"
                        : "bg-gray-100 rounded-2xl rounded-bl-md"
                    } p-4`}
                  >
                    {message.role === "assistant" && !message.isError && (
                      <div className="flex items-center gap-2 mb-2 text-indigo-400">
                        <Bot className="h-4 w-4" />
                        <span className="text-xs font-medium">AI Assistant</span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                    
                    {/* Insight Cards */}
                    {message.insights && message.insights.length > 0 && (
                      <div className="mt-2">
                        {message.insights.map(renderInsightCard)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md p-4">
                    <div className="flex items-center gap-2 text-indigo-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        {/* Quick Suggestions (when conversation started) */}
        {messages.length > 0 && !loading && (
          <div className="px-6 pb-2">
            <div className="flex flex-wrap gap-2">
              {suggestions.slice(0, 4).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-xs px-3 py-1.5 bg-gray-100 rounded-full border border-gray-200 hover:border-indigo-500/50 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Input Area */}
        <div className="p-6 border-t border-gray-200 bg-white/30">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about your business..."
              disabled={loading}
              className="flex-1 bg-gray-100 border-gray-200 h-12"
              data-testid="ai-chat-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 h-12 px-6"
              data-testid="ai-send-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-2 text-center">
            AI responses are based on your POS data and may not be 100% accurate.
          </p>
        </div>
      </div>
    </div>
  );
};

export default POSAIAssistantPage;
